/**
 * Srcset candidate verifier.
 *
 * Confirms two things every time the hero finishes decoding:
 *   1. WHICH candidate the browser actually downloaded (post-`sizes`
 *      resolution) — and whether that is the correct rung for the rendered
 *      slot at the current device pixel ratio.
 *   2. WHETHER the decode stayed inside the target threshold (defaults to the
 *      configured hero budget for the measured breakpoint, which is the
 *      mobile number on phones).
 *
 * Results are persisted to localStorage so /qa/hero/perf can show a history
 * of verifications collected across viewports and network profiles.
 */

import type { HeroPerfReport } from "./hero-perf";
import {
  evaluateHeroBudget,
  loadHeroBudgets,
  type HeroBudgets,
} from "./hero-perf-budgets";
import { bucketBreakpoint, type Breakpoint } from "./hero-perf-samples";

export type SrcSetCandidate = { url: string; width: number };

/** Parses a `srcset` string into width-descriptor candidates, sorted ascending. */
export function parseSrcSet(srcset?: string): SrcSetCandidate[] {
  if (!srcset) return [];
  return srcset
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [url, descriptor] = part.split(/\s+/);
      const width = descriptor?.endsWith("w")
        ? Number(descriptor.slice(0, -1))
        : NaN;
      return { url, width };
    })
    .filter((c) => c.url && Number.isFinite(c.width))
    .sort((a, b) => a.width - b.width);
}

const basename = (url: string) => {
  try {
    return decodeURIComponent(url.split("?")[0].split("/").pop() || url);
  } catch {
    return url;
  }
};

/** Matches the loaded URL back to a candidate (currentSrc is absolute). */
export function matchCandidate(
  currentSrc: string,
  candidates: SrcSetCandidate[],
): SrcSetCandidate | undefined {
  if (!currentSrc) return undefined;
  const chosenName = basename(currentSrc);
  return candidates.find(
    (c) => currentSrc.endsWith(c.url) || basename(c.url) === chosenName,
  );
}

export type CandidateVerdict = "optimal" | "oversized" | "undersized" | "unknown";

export type SrcSetVerification = {
  id: string;
  at: string;
  breakpoint: Breakpoint;
  viewportWidth: number;
  dpr: number;
  /** URL the browser actually loaded. */
  chosenUrl: string;
  chosenFile: string;
  /** Width descriptor of the chosen candidate (falls back to naturalWidth). */
  chosenWidth?: number;
  /** CSS px the img renders at. */
  renderedWidth?: number;
  /** Physical pixels needed = renderedWidth × dpr. */
  requiredWidth?: number;
  /** Smallest candidate that covers `requiredWidth`. */
  expectedWidth?: number;
  expectedFile?: string;
  verdict: CandidateVerdict;
  /** Positive = downloaded more pixels than needed. */
  overshootPct?: number;
  transferKb?: number;
  decodeMs?: number;
  decodeTargetMs: number;
  decodeWithinTarget: boolean;
  /** True when candidate is optimal AND decode is within target. */
  pass: boolean;
  notes: string[];
};

export function verifySrcSetChoice(
  report: HeroPerfReport,
  opts: {
    candidates: SrcSetCandidate[];
    /** Override the decode threshold; defaults to the breakpoint budget. */
    decodeTargetMs?: number;
    budgets?: HeroBudgets;
  },
): SrcSetVerification {
  const budgets = opts.budgets ?? loadHeroBudgets();
  const breakpoint = bucketBreakpoint(report.viewportWidth);
  const decodeTargetMs =
    opts.decodeTargetMs ?? evaluateHeroBudget(report, budgets).budgets.decodeMs;

  const dpr = report.devicePixelRatio || 1;
  const chosenUrl = report.currentSrc || "";
  const matched = matchCandidate(chosenUrl, opts.candidates);
  const chosenWidth = matched?.width ?? report.naturalWidth;
  const renderedWidth = report.renderedWidth;
  const requiredWidth =
    renderedWidth != null ? Math.round(renderedWidth * dpr) : undefined;

  const expected =
    requiredWidth != null
      ? opts.candidates.find((c) => c.width >= requiredWidth) ??
        opts.candidates[opts.candidates.length - 1]
      : undefined;

  const notes: string[] = [];
  let verdict: CandidateVerdict = "unknown";
  if (chosenWidth != null && requiredWidth != null) {
    if (chosenWidth < requiredWidth) {
      verdict = "undersized";
      notes.push(
        `Loaded ${chosenWidth}w for a ${requiredWidth}px slot — the image will look soft.`,
      );
    } else if (expected && chosenWidth > expected.width) {
      verdict = "oversized";
      notes.push(
        `Loaded ${chosenWidth}w where ${expected.width}w covers the slot — check the \`sizes\` attribute.`,
      );
    } else {
      verdict = "optimal";
    }
  } else if (!matched && chosenUrl) {
    notes.push("Loaded URL is not in the provided srcset (fallback rung?).");
  }

  const overshootPct =
    chosenWidth != null && requiredWidth
      ? +(((chosenWidth - requiredWidth) / requiredWidth) * 100).toFixed(0)
      : undefined;

  const decodeMs = report.deltas.decodeMs;
  const decodeWithinTarget = decodeMs == null ? true : decodeMs <= decodeTargetMs;
  if (!decodeWithinTarget) {
    notes.push(
      `Decode ${decodeMs?.toFixed(0)}ms exceeds the ${breakpoint} target of ${decodeTargetMs}ms.`,
    );
  }

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    at: new Date().toISOString(),
    breakpoint,
    viewportWidth: report.viewportWidth,
    dpr: +dpr.toFixed(2),
    chosenUrl,
    chosenFile: basename(chosenUrl),
    chosenWidth,
    renderedWidth,
    requiredWidth,
    expectedWidth: expected?.width,
    expectedFile: expected ? basename(expected.url) : undefined,
    verdict,
    overshootPct,
    transferKb:
      report.transferSizeBytes != null
        ? +(report.transferSizeBytes / 1024).toFixed(1)
        : undefined,
    decodeMs: decodeMs != null ? +decodeMs.toFixed(1) : undefined,
    decodeTargetMs,
    decodeWithinTarget,
    pass: verdict !== "undersized" && verdict !== "oversized" && decodeWithinTarget,
    notes,
  };
}

export const SRCSET_VERIFY_STORAGE_KEY = "hero-srcset-verify-v1";
export const SRCSET_VERIFY_EVENT = "hero-srcset-verified";
const MAX_RECORDS = 200;

export function loadVerifications(): SrcSetVerification[] {
  try {
    const raw = localStorage.getItem(SRCSET_VERIFY_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as SrcSetVerification[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function recordVerification(v: SrcSetVerification) {
  try {
    const next = [v, ...loadVerifications()].slice(0, MAX_RECORDS);
    localStorage.setItem(SRCSET_VERIFY_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(SRCSET_VERIFY_EVENT, { detail: v }),
    );
  }
}

export function clearVerifications() {
  try {
    localStorage.removeItem(SRCSET_VERIFY_STORAGE_KEY);
  } catch {
    /* noop */
  }
}
